import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  EditFieldGovernance,
  VendorProfileDocument,
} from "../types";
import { AttachmentDisplay } from "../components/AttachmentDisplay";
import {
  confirmEditUpload,
  mintEditUpload,
  sendOtp,
  submitEditRequest,
  verifyOtp,
} from "@/services/vendorProfileService";
import { isKnownPath, presentationFor } from "./governanceLabels";
import {
  approverDisplayName,
  FIELD_APPROVER_ROLES,
  FIELD_LABELS,
  requiredReviewStages,
  type ReviewStage,
} from "./changeRequestFormat";
import { ReviewerPickers } from "@/pages/VendorOnboarding/reviewer/ReviewerPickers";
import { type BouncerRoleUser } from "@/services/bouncerUsers";
import type { EditRequestReviewers } from "../types";
import {
  MULTI_VALUE_EMAIL_FIELDS,
  textMaxLengthFor,
  validateFieldValue,
} from "./editValidators";
import { CharCounter } from "@/pages/VendorOnboarding/components/CharCounter";
import { MultiEmailInput } from "./MultiEmailInput";
import { fetchPincodeLocation, isLikelyPincode } from "@/services/pincodeLookup";
import { fetchIfsc, isLikelyIfsc } from "@/services/razorpayIfsc";
import { stateFromGstin } from "@/pages/VendorOnboarding/utils/gstinStateCodes";
import {
  classifyPanLookupError,
  isValidPanFormat,
  lookupPanName,
} from "@/services/panNameLookup";
import {
  classifyGstLookupError,
  isValidGstinFormat,
  lookupGstDetails,
} from "@/services/gstDetailsLookup";
import { COUNTRY_CODES } from "@/pages/VendorOnboarding/fields/PhoneField";
import { PHONE_RULES, validatePhone } from "@/pages/VendorOnboarding/utils/schemaUtils";
import { readErrorMessage } from "@/pages/VendorOnboarding/utils/mapBeErrors";
import { putToGcs } from "@/services/taskerAttachments";
import { verifyBank } from "@/services/bankVerify";
import { useAccountTypes } from "@/pages/VendorOnboarding/hooks/useAccountTypes";
import { useVendorCategories } from "@/pages/VendorOnboarding/hooks/useVendorCategories";
import { subCategoriesOf } from "../utils/vendorCategories";
import { maskAccountNumber, maskPan } from "../utils/masking";
import { DEFAULT_COUNTRY_CODE, formatPhone, splitPhone } from "../utils/phone";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATIC_SELECT_OPTIONS: Record<string, { label: string; value: string }[]> = {
  vendor_type: [{ label: "GST", value: "GST" }],
  credit_days: ["7", "15", "30", "45", "60", "75", "90", "100", "120"].map(
    (d) => ({ label: d, value: d }),
  ),
  payment_terms_pattern: [
    "25-25-25-25",
    "50-50",
    "30-70",
    "0-100",
    "100-0",
    "40-60",
    "25-25-50",
  ].map((p) => ({ label: p, value: p })),
};

const EMAIL_OTP_FIELDS = new Set<string>([
  "email_id",
  "vendor_invoice_portal_access_email",
  "tars_production_process_management_access_email",
]);
const MOBILE_OTP_FIELDS = new Set<string>(["poc_contact"]);
const OTP_LENGTH = 4;
const OTP_RESEND_COOLDOWN_SECONDS = 30;
const OTP_MAX_RESENDS = 3;

function otpChannelFor(fieldName: string): "email" | "mobile" | null {
  if (EMAIL_OTP_FIELDS.has(fieldName)) return "email";
  if (MOBILE_OTP_FIELDS.has(fieldName)) return "mobile";
  return null;
}

export interface EditExtraField {
  fieldName: string;
  label: string;
  currentValue: string;
  placeholder?: string;
  type?: "text" | "date" | "readonly" | "confirm" | "document" | "sub_category";
  matches?: string;
  currentAttachment?: VendorProfileDocument;
  required?: boolean;
  maxDate?: string;
}

const blockClipboard = (e: React.SyntheticEvent) => e.preventDefault();

function toDateInputValue(raw: string): string {
  if (!raw) return "";
  const iso = raw.trim();
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return match ? match[1] : "";
}

function futureDateError(f: EditExtraField, raw: string): string | null {
  if (!f.maxDate || !raw || raw <= f.maxDate) return null;
  if (raw === toDateInputValue(f.currentValue ?? "")) return null;
  return `${f.label} cannot be in the future.`;
}

function appliedToast(
  target: EditTarget | null | undefined,
  applied: string[],
): string {
  const subject =
    !target || applied.length === 0 || applied.includes(target.fieldName)
      ? target?.label
      : applied.map((f) => FIELD_LABELS[f] ?? f).join(", ");
  return `${subject} updated · applied directly · audit logged`;
}

export interface EditTarget {
  facilityId: string;
  fieldName: string;
  label: string;
  title?: string;
  currentValue: unknown;
  governance: EditFieldGovernance;
  kind?: "value" | "document";
  pocCountryCode?: string | null;
  countryCodeField?: string;
  currentAttachmentName?: string | null;
  currentAttachment?: VendorProfileDocument;
  extraFields?: EditExtraField[];
  gstAddressAutofill?: boolean;
}

type Props = {
  target: EditTarget | null;
  onClose: () => void;
  onApplied?: (message: string) => void;
  onNotify?: (message: string) => void;
  isInternal?: boolean;
};

type Phase = "input" | "otp";

interface OtpRow {
  email: string;
  requestId: string;
  otp: string;
  error: string | null;
}

function currentAsArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function MutationErrorAlert({
  error,
  fallback,
}: {
  error: unknown;
  fallback: string;
}) {
  return <Alert severity="error">{readErrorMessage(error, fallback)}</Alert>;
}

export function EditDrawer({
  target,
  onClose,
  onApplied,
  onNotify,
  isInternal = false,
}: Props) {
  const serviceAuthMode: "vendor" | "bouncer" = "bouncer";
  const [newValue, setNewValue] = useState("");
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY_CODE);
  const [initialCountryCode, setInitialCountryCode] = useState<string>(DEFAULT_COUNTRY_CODE);
  const [newEmails, setNewEmails] = useState<string[]>([]);
  const [cm, setCm] = useState<BouncerRoleUser[]>([]);
  const [phase, setPhase] = useState<Phase>("input");
  const [otpRows, setOtpRows] = useState<OtpRow[]>([]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [newDocument, setNewDocument] = useState<File | null>(null);
  const [extraValues, setExtraValues] = useState<Record<string, string>>({});
  const [extraDocuments, setExtraDocuments] = useState<
    Record<string, File | null>
  >({});
  const newDocInputRef = useRef<HTMLInputElement | null>(null);
  const extraDocRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const queryClient = useQueryClient();
  const uploadedAttachments = useRef<
    WeakMap<File, { attachment_id: string; file_name: string }>
  >(new WeakMap());

  const isMulti = target
    ? MULTI_VALUE_EMAIL_FIELDS.has(target.fieldName)
    : false;

  const reviewStages = useMemo(() => {
    if (!target) return new Set<ReviewStage>();
    const names = [
      target.fieldName,
      ...(target.extraFields ?? []).map((f) => f.fieldName),
    ].filter((f) => Object.prototype.hasOwnProperty.call(FIELD_APPROVER_ROLES, f));
    return requiredReviewStages(names);
  }, [target]);
  const showCmPicker = isInternal && reviewStages.has("CM_REVIEW");
  const reviewersMissing = showCmPicker && cm.length === 0;
  const reviewers: EditRequestReviewers | undefined =
    showCmPicker && cm.length
      ? { category_manager_email: cm.map((u) => u.email_id) }
      : undefined;
  const pickedApproverRole = showCmPicker
    ? "CM"
    : isInternal && reviewStages.has("FINANCE_REVIEW")
      ? "FINANCE"
      : undefined;
  const vmDirect =
    isInternal &&
    reviewStages.size > 0 &&
    [...reviewStages].every((s) => s === "VM_REVIEW");

  useEffect(() => {
    if (target) {
      if (target.fieldName === "poc_contact") {
        const current = splitPhone(
          String(target.currentValue ?? ""),
          target.pocCountryCode,
        );
        setCountryCode(current.countryCode);
        setInitialCountryCode(current.countryCode);
        setNewValue(current.number);
      } else {
        setCountryCode(DEFAULT_COUNTRY_CODE);
        setInitialCountryCode(DEFAULT_COUNTRY_CODE);
        setNewValue(isMulti ? "" : String(target.currentValue ?? ""));
      }
      setNewEmails(isMulti ? currentAsArray(target.currentValue) : []);
      setPhase("input");
      setOtpRows([]);
      setResendCooldown(0);
      setResendCount(0);
      setNewDocument(null);
      setExtraDocuments({});
      setCm([]);
      uploadedAttachments.current = new WeakMap();
      submitMutation.reset();
      sendOtpMutation.reset();
      verifyAndSubmitMutation.reset();
      const seed: Record<string, string> = {};
      (target.extraFields ?? []).forEach((f) => {
        const raw = f.currentValue ?? "";
        if (f.type === "confirm") {
          seed[f.fieldName] = "";
        } else if (f.type === "date") {
          seed[f.fieldName] = toDateInputValue(raw);
        } else {
          seed[f.fieldName] = raw;
        }
      });
      setExtraValues(seed);

      const initialAcc =
        target.fieldName === "account_number"
          ? String(target.currentValue ?? "").trim()
          : (seed.account_number ?? "").trim();
      const initialIfsc =
        target.fieldName === "ifsc_code"
          ? String(target.currentValue ?? "").trim().toUpperCase()
          : (seed.ifsc_code ?? "").trim().toUpperCase();
      const initialPincode =
        target.fieldName === "location_pincode"
          ? String(target.currentValue ?? "").trim()
          : (seed.location_pincode ?? "").trim();
      const initialPan =
        target.fieldName === "pan_number"
          ? String(target.currentValue ?? "").trim().toUpperCase()
          : "";
      lastResolvedPincode.current = initialPincode || null;
      lastResolvedIfsc.current = initialIfsc || null;
      lastResolvedPan.current = initialPan || null;
      lastVerifiedPair.current =
        initialAcc && initialIfsc ? `${initialAcc}|${initialIfsc}` : null;
      const initialCity = (seed.billing_city ?? "").trim();
      const initialCountry = (seed.billing_country ?? "").trim();
      setPincodeStatus(
        initialPincode && initialCity && initialCountry ? "resolved" : "idle",
      );
      const initialBankName = (seed.bank_name ?? "").trim();
      const initialBranchName = (seed.branch_name ?? "").trim();
      setIfscStatus(
        initialIfsc && initialBankName && initialBranchName ? "resolved" : "idle",
      );
      setPennyDropStatus("idle");
      setPennyDropError(null);
      setPanLookupStatus("idle");
      setPanLookupError(null);
      lastResolvedGst.current = (seed.gst ?? "").trim().toUpperCase() || null;
      gstFilledRef.current = { line: false, pincode: false };
      setGstFilled(gstFilledRef.current);
      setGstLookupStatus("idle");
      setGstLookupError(null);
    }
  }, [target, isMulti]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const cleanedEmails = useMemo(
    () => newEmails.map((e) => e.trim()).filter(Boolean),
    [newEmails],
  );

  const pincodeSource: string = useMemo(() => {
    if (!target) return "";
    if (target.fieldName === "location_pincode") return newValue.trim();
    if ((target.extraFields ?? []).some((f) => f.fieldName === "location_pincode")) {
      return (extraValues.location_pincode ?? "").trim();
    }
    return "";
  }, [target, newValue, extraValues]);

  useEffect(() => {
    if (!target) return;
    const hasBillingStateExtra = (target.extraFields ?? []).some(
      (f) => f.fieldName === "billing_state",
    );
    if (!hasBillingStateExtra) return;
    const gst = (extraValues.gst ?? "").trim().toUpperCase();
    const derived = stateFromGstin(gst);
    setExtraValues((prev) =>
      prev.billing_state === (derived ?? "")
        ? prev
        : { ...prev, billing_state: derived ?? "" },
    );
  }, [target, extraValues.gst]);

  const lastResolvedPan = useRef<string | null>(null);
  const [panLookupStatus, setPanLookupStatus] = useState<
    "idle" | "resolving" | "resolved" | "failed"
  >("idle");
  const [panLookupError, setPanLookupError] = useState<string | null>(null);
  useEffect(() => {
    if (!target) return;
    if (target.fieldName !== "pan_number") return;
    const pan = newValue.trim().toUpperCase();
    if (!isValidPanFormat(pan)) {
      setPanLookupStatus("idle");
      setPanLookupError(null);
      return;
    }
    if (lastResolvedPan.current === pan) return;
    let cancelled = false;
    setPanLookupStatus("resolving");
    setPanLookupError(null);
    const timer = setTimeout(async () => {
      try {
        const res = await lookupPanName(pan, { auth: serviceAuthMode });
        if (cancelled) return;
        lastResolvedPan.current = pan;
        if (res.valid && res.legalName) {
          setPanLookupStatus("resolved");
          setExtraValues((prev) =>
            prev.legal_name === res.legalName
              ? prev
              : { ...prev, legal_name: res.legalName! },
          );
        } else {
          setPanLookupStatus("failed");
          setPanLookupError("PAN not found. Please re-check the number.");
        }
      } catch (err) {
        if (cancelled) return;
        const info = classifyPanLookupError(err);
        if (info.kind === "unavailable") {
          setPanLookupStatus("idle");
          setPanLookupError(null);
          return;
        }
        setPanLookupStatus("failed");
        setPanLookupError(info.message);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [target, newValue, serviceAuthMode]);

  const lastResolvedGst = useRef<string | null>(null);
  const gstFilledRef = useRef({ line: false, pincode: false });
  const [gstFilled, setGstFilled] = useState(gstFilledRef.current);
  const [gstLookupStatus, setGstLookupStatus] = useState<
    "idle" | "resolving" | "resolved" | "failed"
  >("idle");
  const [gstLookupError, setGstLookupError] = useState<string | null>(null);
  useEffect(() => {
    if (!target) return;
    if (target.fieldName !== "vendor_type") return;
    const hasAddressExtra = (target.extraFields ?? []).some(
      (f) => f.fieldName === "address_billing",
    );
    if (!hasAddressExtra || !target.gstAddressAutofill) return;
    const gst = (extraValues.gst ?? "").trim().toUpperCase();
    const revertGstFill = () => {
      const filled = gstFilledRef.current;
      if (!filled.line && !filled.pincode) return;
      gstFilledRef.current = { line: false, pincode: false };
      setGstFilled(gstFilledRef.current);
      const seedFor = (name: string) =>
        (target.extraFields ?? []).find((f) => f.fieldName === name)
          ?.currentValue ?? "";
      if (filled.pincode) {
        const seedPincode = seedFor("location_pincode").trim();
        lastResolvedPincode.current = seedPincode || null;
        setPincodeStatus(
          seedPincode && seedFor("billing_city").trim() && seedFor("billing_country").trim()
            ? "resolved"
            : "idle",
        );
      }
      setExtraValues((prev) => ({
        ...prev,
        ...(filled.line ? { address_billing: seedFor("address_billing") } : {}),
        ...(filled.pincode
          ? {
              location_pincode: seedFor("location_pincode"),
              billing_city: seedFor("billing_city"),
              billing_country: seedFor("billing_country"),
            }
          : {}),
      }));
    };
    if (!isValidGstinFormat(gst)) {
      revertGstFill();
      setGstLookupStatus("idle");
      setGstLookupError(null);
      return;
    }
    if (lastResolvedGst.current === gst) return;
    let cancelled = false;
    setGstLookupStatus("resolving");
    setGstLookupError(null);
    const timer = setTimeout(async () => {
      try {
        const res = await lookupGstDetails(gst, { auth: serviceAuthMode });
        if (cancelled) return;
        lastResolvedGst.current = gst;
        if (!res.valid) {
          revertGstFill();
          setGstLookupStatus("failed");
          setGstLookupError(
            res.status
              ? `GSTIN is not active (status: ${res.status}).`
              : "GSTIN not found. Please re-check the number.",
          );
          return;
        }
        const address = res.address;
        const line = address
          ? [address.line1, address.line2].filter(Boolean).join(", ")
          : "";
        const pincode = address?.pincode ?? "";
        revertGstFill();
        if (!line && !pincode) {
          setGstLookupStatus("idle");
          return;
        }
        gstFilledRef.current = { line: Boolean(line), pincode: Boolean(pincode) };
        setGstFilled(gstFilledRef.current);
        setGstLookupStatus("resolved");
        setExtraValues((prev) => ({
          ...prev,
          ...(line ? { address_billing: line } : {}),
          ...(pincode ? { location_pincode: pincode } : {}),
        }));
      } catch (err) {
        if (cancelled) return;
        revertGstFill();
        const info = classifyGstLookupError(err);
        if (info.kind === "unavailable") {
          setGstLookupStatus("idle");
          setGstLookupError(null);
          return;
        }
        setGstLookupStatus("failed");
        setGstLookupError(info.message);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [target, extraValues.gst, serviceAuthMode]);

  const lastResolvedPincode = useRef<string | null>(null);
  const [pincodeStatus, setPincodeStatus] = useState<
    "idle" | "resolving" | "resolved" | "failed"
  >("idle");
  useEffect(() => {
    if (!target) return;
    const hasCityOrCountry = (target.extraFields ?? []).some(
      (f) => f.fieldName === "billing_city" || f.fieldName === "billing_country",
    );
    if (!hasCityOrCountry) return;
    if (!pincodeSource || !isLikelyPincode(pincodeSource)) {
      if (lastResolvedPincode.current) {
        lastResolvedPincode.current = null;
      }
      setPincodeStatus("idle");
      return;
    }
    if (lastResolvedPincode.current === pincodeSource) return;
    let cancelled = false;
    setPincodeStatus("resolving");
    const timer = setTimeout(async () => {
      const data = await fetchPincodeLocation(pincodeSource, {
        auth: serviceAuthMode,
      });
      if (cancelled) return;
      if (!data) {
        setPincodeStatus("failed");
        return;
      }
      lastResolvedPincode.current = pincodeSource;
      setPincodeStatus("resolved");
      setExtraValues((prev) => ({
        ...prev,
        billing_city: data.city,
        billing_country: data.country_code,
      }));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pincodeSource, target]);

  const ifscSource: string = useMemo(() => {
    if (!target) return "";
    if (target.fieldName === "ifsc_code") return newValue.trim().toUpperCase();
    if ((target.extraFields ?? []).some((f) => f.fieldName === "ifsc_code")) {
      return (extraValues.ifsc_code ?? "").trim().toUpperCase();
    }
    return "";
  }, [target, newValue, extraValues]);

  const accountSource: string = useMemo(() => {
    if (!target) return "";
    if (target.fieldName === "account_number") return newValue.trim();
    if ((target.extraFields ?? []).some((f) => f.fieldName === "account_number")) {
      return (extraValues.account_number ?? "").trim();
    }
    return "";
  }, [target, newValue, extraValues]);

  const lastResolvedIfsc = useRef<string | null>(null);
  const currentIfscRef = useRef<string>(ifscSource);
  currentIfscRef.current = ifscSource;
  const [ifscStatus, setIfscStatus] = useState<
    "idle" | "resolving" | "resolved" | "failed"
  >("idle");
  useEffect(() => {
    if (!target) return;
    const hasBankNameOrBranch = (target.extraFields ?? []).some(
      (f) => f.fieldName === "bank_name" || f.fieldName === "branch_name",
    );
    if (!hasBankNameOrBranch) return;
    if (lastResolvedIfsc.current && lastResolvedIfsc.current !== ifscSource) {
      lastResolvedIfsc.current = null;
      setExtraValues((prev) => ({ ...prev, bank_name: "", branch_name: "" }));
      setIfscStatus("idle");
    }
    if (!ifscSource) {
      setIfscStatus("idle");
      return;
    }
    if (!isLikelyIfsc(ifscSource)) {
      setIfscStatus("idle");
      return;
    }
    if (lastResolvedIfsc.current === ifscSource) return;
    let cancelled = false;
    setIfscStatus("resolving");
    const timer = setTimeout(async () => {
      const capturedIfsc = ifscSource;
      const data = await fetchIfsc(capturedIfsc);
      if (cancelled) return;
      if (currentIfscRef.current !== capturedIfsc) return;
      if (!data) {
        setIfscStatus("failed");
        return;
      }
      lastResolvedIfsc.current = capturedIfsc;
      setIfscStatus("resolved");
      setExtraValues((prev) => ({
        ...prev,
        bank_name: data.BANK ?? "",
        branch_name: data.BRANCH ?? "",
      }));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ifscSource, target]);

  const lastVerifiedPair = useRef<string | null>(null);
  const pennyDropTargetRef = useRef<EditTarget | null>(null);
  const [pennyDropStatus, setPennyDropStatus] = useState<
    "idle" | "verifying" | "verified" | "failed"
  >("idle");
  const [pennyDropError, setPennyDropError] = useState<string | null>(null);

  const hasAccountTypeExtra = (target?.extraFields ?? []).some(
    (f) => f.fieldName === "account_type",
  );
  const accountTypesQuery = useAccountTypes(serviceAuthMode, {
    enabled: hasAccountTypeExtra,
  });
  const accountTypeOptions = accountTypesQuery.data ?? [];
  const isProfileCategory = target?.fieldName === "super_category";
  const vendorCategoriesQuery = useVendorCategories({
    enabled: isProfileCategory,
  });
  const vendorCategories = vendorCategoriesQuery.data ?? [];
  const subCategoryOptions = subCategoriesOf(vendorCategories, newValue);
  useEffect(() => {
    if (!target) return;
    const hasAccountName = (target.extraFields ?? []).some(
      (f) => f.fieldName === "account_name",
    );
    if (!hasAccountName) return;
    const freshTarget = pennyDropTargetRef.current !== target;
    pennyDropTargetRef.current = target;
    const acc = accountSource;
    const ifs = ifscSource;
    const validAcc = !!acc && /^\d{6,18}$/.test(acc);
    const validIfs = isLikelyIfsc(ifs);
    const pair = validAcc && validIfs ? `${acc}|${ifs}` : null;
    if (
      !freshTarget &&
      lastVerifiedPair.current &&
      lastVerifiedPair.current !== pair
    ) {
      lastVerifiedPair.current = null;
      setPennyDropStatus("idle");
      setPennyDropError(null);
      setExtraValues((prev) => ({ ...prev, account_name: "" }));
    }
    if (!pair || lastVerifiedPair.current === pair) return;
    let cancelled = false;
    setPennyDropStatus("verifying");
    setPennyDropError(null);
    const timer = setTimeout(async () => {
      try {
        const res = await verifyBank(
          { account_number: acc, ifsc: ifs },
          { auth: serviceAuthMode },
        );
        if (cancelled) return;
        if (res.valid && res.nameAtBank) {
          lastVerifiedPair.current = pair;
          setPennyDropStatus("verified");
          setExtraValues((prev) => ({ ...prev, account_name: res.nameAtBank! }));
        } else {
          setPennyDropStatus("failed");
          setPennyDropError(
            res.message ??
              "Bank account verification failed. Check the account number and IFSC.",
          );
        }
      } catch {
        if (cancelled) return;
        setPennyDropStatus("failed");
        setPennyDropError(
          "Could not verify bank account right now. Try again in a moment.",
        );
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [accountSource, ifscSource, target]);

  const newDocumentUrl = useMemo(
    () => (newDocument ? URL.createObjectURL(newDocument) : null),
    [newDocument],
  );
  useEffect(() => {
    return () => {
      if (newDocumentUrl) URL.revokeObjectURL(newDocumentUrl);
    };
  }, [newDocumentUrl]);

  const extraDocumentUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    for (const [k, file] of Object.entries(extraDocuments)) {
      if (file) urls[k] = URL.createObjectURL(file);
    }
    return urls;
  }, [extraDocuments]);
  useEffect(() => {
    return () => {
      for (const url of Object.values(extraDocumentUrls)) {
        URL.revokeObjectURL(url);
      }
    };
  }, [extraDocumentUrls]);

  const isDocument = target?.kind === "document";
  const extraFieldDefs = target?.extraFields ?? [];
  const normalizeExtra = (f: EditExtraField, raw: string) =>
    f.type === "date" ? toDateInputValue(raw) : raw.trim();
  const extraFieldsChanged = extraFieldDefs.some((f) => {
    if (f.type === "confirm") return false;
    if (f.type === "document") return Boolean(extraDocuments[f.fieldName]);
    const cur = normalizeExtra(f, f.currentValue ?? "");
    const next = normalizeExtra(f, extraValues[f.fieldName] ?? "");
    return next !== cur;
  });

  const resolveMatchValue = (matches: string | undefined): string => {
    if (!target || !matches) return "";
    if (matches === "__primary__") return newValue.trim();
    return (extraValues[matches] ?? "").trim();
  };
  const confirmError = (() => {
    for (const f of extraFieldDefs) {
      if (f.type !== "confirm") continue;
      const entered = (extraValues[f.fieldName] ?? "").trim();
      if (!entered) return `${f.label} is required.`;
      if (entered !== resolveMatchValue(f.matches)) {
        return "Should match with new value";
      }
    }
    return null;
  })();

  // poc_contact has two wire shapes. Legacy (no countryCodeField on the target):
  // one string "+CC<digits>", read back via splitPhone's prefix parse (default
  // +91). New contract (backend advertises poc_contact_country_code): digits in
  // poc_contact + the code in countryCodeField, seeded from the stored code —
  // see buildValueChanges. Change detection compares digits AND countryCode, so
  // a CC-only edit still dirties the field.
  const submitPayload = isMulti
    ? cleanedEmails
    : target?.fieldName === "poc_contact"
      ? `${countryCode}${newValue.trim()}`
      : newValue.trim();
  // Retry-safe attachment upload: cache the confirmed shape per File instance
  // so a submitEditRequest failure followed by a retry doesn't re-upload the
  // same bytes (which would strand the previous GCS blob). Cleared on target
  // change; naturally invalidated when the user picks a different File.
  const uploadPickedFile = async (
    facilityId: string,
    file: File,
  ): Promise<{ attachment_id: string; file_name: string }> => {
    const cached = uploadedAttachments.current.get(file);
    if (cached) return cached;
    const slot = await mintEditUpload(facilityId, file.name, serviceAuthMode);
    await putToGcs(slot.url, file);
    const confirmed = await confirmEditUpload(
      facilityId,
      { attachment_id: slot.attachment_id, file_name: file.name },
      serviceAuthMode,
    );
    const result = {
      attachment_id: confirmed.attachment_id,
      file_name: confirmed.file_name || file.name,
    };
    uploadedAttachments.current.set(file, result);
    return result;
  };

  const buildDocumentChanges = async (): Promise<Record<string, unknown>> => {
    if (!target) return {};
    const changes: Record<string, unknown> = {};
    const uploads: Array<[string, File]> = [];
    if (newDocument) uploads.push([target.fieldName, newDocument]);
    for (const f of extraFieldDefs) {
      if (f.type === "confirm") continue;
      if (f.type === "document") {
        const file = extraDocuments[f.fieldName];
        if (file) uploads.push([f.fieldName, file]);
        continue;
      }
      const cur = normalizeExtra(f, f.currentValue ?? "");
      const next = normalizeExtra(f, extraValues[f.fieldName] ?? "");
      if (next !== cur) {
        changes[f.fieldName] = next;
      }
    }
    const { facilityId } = target;
    const uploaded = await Promise.all(
      uploads.map(([name, file]) =>
        uploadPickedFile(facilityId, file).then(
          (result) => [name, result] as const,
        ),
      ),
    );
    for (const [name, result] of uploaded) changes[name] = result;
    return changes;
  };

  const addedEmails = useMemo(() => {
    if (!isMulti || !target) return [] as string[];
    const currentLc = new Set(
      currentAsArray(target.currentValue).map((s) => s.toLowerCase()),
    );
    return cleanedEmails.filter((e) => !currentLc.has(e.toLowerCase()));
  }, [isMulti, target, cleanedEmails]);

  const buildValueChanges = async (): Promise<Record<string, unknown>> => {
    if (!target) return {};
    const changes: Record<string, unknown> = {};
    const primaryChanged = isMulti
      ? (() => {
          const currentArr = currentAsArray(target.currentValue);
          const norm = (arr: string[]) =>
            [...arr].map((s) => s.toLowerCase()).sort().join(",");
          return norm(cleanedEmails) !== norm(currentArr);
        })()
      : target.fieldName === "poc_contact"
        ? countryCode !== initialCountryCode ||
          newValue.trim() !==
            splitPhone(String(target.currentValue ?? ""), target.pocCountryCode).number
        : newValue.trim() !== String(target.currentValue ?? "").trim();
    if (primaryChanged) {
      if (target.fieldName === "poc_contact" && target.countryCodeField) {
        changes.poc_contact = newValue.trim();
        changes[target.countryCodeField] = countryCode;
      } else {
        changes[target.fieldName] = submitPayload;
      }
    }
    const uploads: Array<[string, File]> = [];
    for (const f of extraFieldDefs) {
      if (f.type === "confirm") continue;
      if (f.type === "document") {
        const file = extraDocuments[f.fieldName];
        if (file) uploads.push([f.fieldName, file]);
        continue;
      }
      const cur = normalizeExtra(f, f.currentValue ?? "");
      const next = normalizeExtra(f, extraValues[f.fieldName] ?? "");
      if (next !== cur) changes[f.fieldName] = next;
    }
    const { facilityId } = target;
    const uploaded = await Promise.all(
      uploads.map(([name, file]) =>
        uploadPickedFile(facilityId, file).then(
          (result) => [name, result] as const,
        ),
      ),
    );
    for (const [name, result] of uploaded) changes[name] = result;
    return changes;
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error("no target");
      const changes = await (isDocument
        ? buildDocumentChanges()
        : buildValueChanges());
      return submitEditRequest(
        target.facilityId,
        { changes, ...(reviewers ? { reviewers } : {}) },
        serviceAuthMode,
      );
    },
    onSuccess: (data) => {
      const path = target?.governance.path;
      queryClient.invalidateQueries({ queryKey: ["vendor-profile"] });
      const applied = data.applied ?? [];
      const pending = data.pending ?? [];
      if (path === "A" || (applied.length > 0 && pending.length === 0)) {
        onApplied?.(appliedToast(target, applied));
        onClose();
        return;
      }
      const code = pending[0]?.code;
      const approver =
        pending[0]?.approver_role ||
        pickedApproverRole ||
        target?.governance.approver_role;
      onApplied?.(
        `Request ${code ?? "—"} raised · with ${approverDisplayName(approver)}`,
      );
      onClose();
    },
  });

  const sendOtpMutation = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error("no target");
      const channel = otpChannelFor(target.fieldName);
      if (!channel) throw new Error("OTP not supported for this field");
      const targets = isMulti ? addedEmails : [newValue.trim()].filter(Boolean);
      if (targets.length === 0) {
        throw new Error("Enter a new email before requesting a code.");
      }
      const results = await Promise.all(
        targets.map((t) =>
          sendOtp(
            {
              channel,
              target: t,
              ...(channel === "mobile"
                ? { country_code: countryCode }
                : { facility_id: target.facilityId, field: target.fieldName }),
            },
            serviceAuthMode,
          ),
        ),
      );
      return targets.map<OtpRow>((email, i) => ({
        email,
        requestId: results[i].request_id,
        otp: "",
        error: null,
      }));
    },
    onSuccess: (rows) => {
      const isResend = phase === "otp";
      setOtpRows(rows);
      setPhase("otp");
      setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
      if (isResend) setResendCount((c) => c + 1);
      onNotify?.(
        rows.length === 1
          ? "Verification code sent"
          : `Verification codes sent to ${rows.length} email addresses`,
      );
    },
  });

  const verifyAndSubmitMutation = useMutation({
    mutationFn: async () => {
      if (!target || otpRows.length === 0) {
        throw new Error("Missing request. Please resend the code.");
      }
      const results = await Promise.all(
        otpRows.map((row) =>
          verifyOtp(
            { request_id: row.requestId, otp: row.otp },
            serviceAuthMode,
          ),
        ),
      );
      const failedIdx = results
        .map((r, i) => (r.verified ? -1 : i))
        .filter((i) => i >= 0);
      if (failedIdx.length > 0) {
        const failedSet = new Set(failedIdx);
        setOtpRows((prev) =>
          prev.map((row, i) => ({
            ...row,
            error: failedSet.has(i)
              ? "Invalid verification code. Please try again."
              : row.error,
          })),
        );
        throw new Error("One or more codes are invalid. Please try again.");
      }
      const changes = await buildValueChanges();
      if (!target.countryCodeField) {
        changes[target.fieldName] = submitPayload;
      }
      return submitEditRequest(
        target.facilityId,
        { changes, ...(reviewers ? { reviewers } : {}) },
        serviceAuthMode,
      );
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["vendor-profile"] });
      const applied = data.applied ?? [];
      const pending = data.pending ?? [];
      if (applied.length > 0 && pending.length === 0) {
        onApplied?.(appliedToast(target, applied));
        onClose();
        return;
      }
      const code = pending[0]?.code;
      const approver =
        pending[0]?.approver_role ||
        pickedApproverRole ||
        target?.governance.approver_role;
      onApplied?.(
        `Request ${code ?? "—"} raised · with ${approverDisplayName(approver)}`,
      );
      onClose();
    },
  });

  if (!target) return null;
  const gov = target.governance;
  const primaryMax = textMaxLengthFor(target.fieldName, gov);
  const pres = presentationFor(gov.path);
  const unknownPath = !isKnownPath(gov.path);
  const currentAsString = String(target.currentValue ?? "").trim();

  const extraValidationError: string | null = (() => {
    for (const f of extraFieldDefs) {
      if (f.type === "confirm") continue;
      if (f.type === "document") {
        const hasCurrent = Boolean(f.currentAttachment);
        const hasPicked = Boolean(extraDocuments[f.fieldName]);
        if (!hasCurrent && !hasPicked) return `${f.label} is required.`;
        continue;
      }
      const raw = (extraValues[f.fieldName] ?? "").trim();
      if (f.fieldName === "account_type" || f.type === "sub_category") {
        const currentRaw = (f.currentValue ?? "").trim();
        if ((f.required || currentRaw) && !raw) return `${f.label} is required.`;
        continue;
      }
      if (f.type === "readonly") {
        const currentRaw = (f.currentValue ?? "").trim();
        if ((f.required || currentRaw) && !raw) return `${f.label} is required.`;
        continue;
      }
      if (f.type === "date") {
        const currentRaw = (f.currentValue ?? "").trim();
        if ((f.required || currentRaw) && !raw) return `${f.label} is required.`;
        const futureErr = futureDateError(f, raw);
        if (futureErr) return futureErr;
        continue;
      }
      const extraLimit = textMaxLengthFor(f.fieldName);
      if (raw && extraLimit && raw.length > extraLimit) {
        return `${f.label} must be ${extraLimit} characters or fewer.`;
      }
      const err = raw
        ? validateFieldValue(f.fieldName, raw)
        : `${f.label} is required.`;
      if (err) return err;
    }
    return null;
  })();

  const pathIsLocked =
    gov.path === "BLOCKED" || gov.path === "SYSTEM" || unknownPath;
  let isSameValue: boolean;
  let validationError: string | null = null;
  let submitDisabled: boolean;
  if (isDocument) {
    isSameValue = !newDocument && !extraFieldsChanged;
    submitDisabled =
      isSameValue ||
      Boolean(confirmError) ||
      Boolean(extraValidationError) ||
      submitMutation.isLoading ||
      pathIsLocked;
  } else if (isMulti) {
    const currentArr = currentAsArray(target.currentValue);
    const norm = (arr: string[]) =>
      [...arr].map((s) => s.toLowerCase()).sort().join(",");
    isSameValue =
      cleanedEmails.length > 0 && norm(cleanedEmails) === norm(currentArr);
    const badEmail = cleanedEmails.find((e) => !EMAIL_RE.test(e));
    if (cleanedEmails.length === 0) {
      validationError = null;
    } else if (badEmail) {
      validationError = `"${badEmail}" is not a valid email address.`;
    } else {
      const dupe = cleanedEmails.find(
        (e, i) => cleanedEmails.findIndex((x) => x.toLowerCase() === e.toLowerCase()) !== i,
      );
      if (dupe) validationError = "Duplicate email address in the list.";
    }
    submitDisabled =
      cleanedEmails.length === 0 ||
      Boolean(validationError) ||
      isSameValue ||
      submitMutation.isLoading ||
      pathIsLocked;
  } else {
    const primarySame =
      target.fieldName === "poc_contact"
        ? countryCode === initialCountryCode &&
          newValue.trim() === splitPhone(currentAsString, target.pocCountryCode).number
        : newValue.trim() === currentAsString;
    isSameValue = primarySame && !extraFieldsChanged;
    if (target.fieldName === "poc_contact") {
      validationError = newValue.trim()
        ? validatePhone(newValue, countryCode)
        : null;
    } else {
      validationError = newValue.trim()
        ? validateFieldValue(target.fieldName, newValue)
        : null;
    }
    if (!validationError && primaryMax && newValue.length > primaryMax) {
      validationError = `Must be ${primaryMax} characters or fewer.`;
    }
    if (
      !validationError &&
      target.fieldName === "ifsc_code" &&
      ifscStatus === "failed"
    ) {
      validationError = "IFSC code not found. Check the code and try again.";
    }
    submitDisabled =
      !newValue.trim() ||
      isSameValue ||
      Boolean(validationError) ||
      Boolean(extraValidationError) ||
      Boolean(confirmError) ||
      submitMutation.isLoading ||
      pathIsLocked;
  }

  submitDisabled = submitDisabled || reviewersMissing;

  const verifyDisabled =
    verifyAndSubmitMutation.isLoading ||
    sendOtpMutation.isLoading ||
    otpRows.length === 0 ||
    otpRows.some((r) => r.otp.length !== OTP_LENGTH);

  const isReviewOnlyB =
    gov.path === "B" && !otpChannelFor(target.fieldName);
  const direct = presentationFor("A");
  const vmDirectNoOtp = vmDirect && !otpChannelFor(target.fieldName);
  const chipLabel = vmDirectNoOtp
    ? direct.chipLabel
    : isReviewOnlyB
      ? "Needs approval"
      : pres.chipLabel;
  const chipBg = vmDirectNoOtp ? direct.chipBg : isReviewOnlyB ? "#E4E9FE" : pres.chipBg;
  const chipFg = vmDirectNoOtp ? direct.chipFg : isReviewOnlyB ? "#2B44C1" : pres.chipFg;
  const inputExplanation = vmDirectNoOtp
    ? "As the Vendor Manager you don't need to approve your own edit — this change is validated and saved right away."
    : isReviewOnlyB
      ? "This change is sent for approval. Once approved it is applied to your profile and connected systems."
      : pres.explanation;
  const primaryActionLabel = vmDirectNoOtp
    ? direct.primaryActionLabel
    : isReviewOnlyB
      ? "Send for approval"
      : pres.primaryActionLabel;

  const mutationInFlight =
    submitMutation.isLoading ||
    sendOtpMutation.isLoading ||
    verifyAndSubmitMutation.isLoading;
  const guardedClose = () => {
    if (mutationInFlight) return;
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(target)}
      onClose={guardedClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}
    >
      <Box
        sx={{
          px: 4,
          pt: 4,
          pb: 5,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          height: "100%",
          overflowY: "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            {target.title ?? `Edit · ${target.label}`}
          </Typography>
          <IconButton
            size="small"
            onClick={guardedClose}
            disabled={mutationInFlight}
            aria-label="Close drawer"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {phase === "input" && (
          <>
            <Box>
              <Chip
                size="small"
                label={chipLabel}
                sx={{
                  bgcolor: chipBg,
                  color: chipFg,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  height: 26,
                  px: 0.5,
                }}
              />
              <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1 }}>
                {inputExplanation}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
                Current value
              </Typography>
              {isDocument ? (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "#E0E4EF",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1.25,
                    bgcolor: "#F5F7FC",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: 14,
                  }}
                >
                  {target.currentAttachment ? (
                    <AttachmentDisplay document={target.currentAttachment} />
                  ) : (
                    <>
                      <AttachFileIcon
                        sx={{ fontSize: 18, color: "text.secondary" }}
                      />
                      <Box component="span" sx={{ color: "text.secondary" }}>
                        {target.currentAttachmentName || "—"}
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "#E0E4EF",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1.25,
                    bgcolor: "#F5F7FC",
                    fontSize: 14,
                    color: "text.secondary",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {isMulti
                    ? currentAsArray(target.currentValue).join(", ") || "—"
                    : target.fieldName === "account_number"
                      ? maskAccountNumber(String(target.currentValue ?? ""))
                      : target.fieldName === "pan_number"
                        ? maskPan(String(target.currentValue ?? ""))
                        : target.fieldName === "poc_contact"
                          ? formatPhone(String(target.currentValue ?? ""), target.pocCountryCode) || "—"
                          : String(target.currentValue ?? "—")}
                </Box>
              )}
            </Box>

            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
                New value
              </Typography>
              {isDocument ? (
                <>
                  <input
                    ref={newDocInputRef}
                    type="file"
                    hidden
                    onChange={(e) =>
                      setNewDocument(e.target.files?.[0] ?? null)
                    }
                    aria-label={`New value for ${target.label}`}
                  />
                  {newDocument && newDocumentUrl ? (
                    <Box
                      sx={{
                        border: 1,
                        borderColor: "#E0E4EF",
                        borderRadius: 1,
                        px: 1.5,
                        py: 1.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        fontSize: 14,
                      }}
                    >
                      <AttachmentDisplay
                        document={{
                          id: -1,
                          owner_type: "vendor",
                          owner_id: "",
                          document_type: target.fieldName,
                          service_provider: "local",
                          bucket: "",
                          file: newDocument.name,
                          download_url: newDocumentUrl,
                        }}
                        allowBlobUrl
                      />
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => newDocInputRef.current?.click()}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 12,
                          minWidth: 0,
                        }}
                        aria-label={`Change ${target.label}`}
                      >
                        Change
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => newDocInputRef.current?.click()}
                      aria-label={`Upload new ${target.label}`}
                      sx={{
                        border: 1,
                        borderColor: "#E0E4EF",
                        borderRadius: 1,
                        px: 1.5,
                        py: 1.25,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        color: "text.secondary",
                        fontSize: 14,
                        textAlign: "left",
                        bgcolor: "transparent",
                        font: "inherit",
                        width: "100%",
                        "&:hover": { borderColor: "primary.main" },
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                        },
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 18 }} />
                      {target.currentAttachmentName || "Choose a file"}
                    </Box>
                  )}
                </>
              ) : isMulti ? (
                <MultiEmailInput
                  value={newEmails}
                  onChange={setNewEmails}
                  ariaLabelPrefix={`New value for ${target.label}`}
                />
              ) : target.fieldName === "poc_contact" ? (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    select
                    size="small"
                    value={countryCode}
                    onChange={(e) => {
                      const next = e.target.value;
                      const nextMax = PHONE_RULES[next]?.length ?? 15;
                      setCountryCode(next);
                      setNewValue((prev) => prev.slice(0, nextMax));
                    }}
                    sx={{ width: 130, flexShrink: 0 }}
                    inputProps={{ "aria-label": "Country code" }}
                  >
                    {COUNTRY_CODES.map((cc) => (
                      <MenuItem key={cc.code} value={cc.code}>
                        {cc.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    type="tel"
                    value={newValue}
                    onChange={(e) => {
                      const maxLen = PHONE_RULES[countryCode]?.length ?? 15;
                      const cleaned = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, maxLen);
                      setNewValue(cleaned);
                    }}
                    autoFocus
                    error={Boolean(validationError)}
                    helperText={validationError ?? " "}
                    inputProps={{
                      "aria-label": `New value for ${target.label}`,
                      inputMode: "numeric",
                      maxLength: PHONE_RULES[countryCode]?.length ?? 15,
                    }}
                  />
                </Box>
              ) : isProfileCategory ? (
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={newValue}
                  onChange={(e) => {
                    const next = e.target.value;
                    setNewValue(next);
                    const storedSub =
                      extraFieldDefs.find((f) => f.fieldName === "sub_category")
                        ?.currentValue ?? "";
                    setExtraValues((prev) => ({
                      ...prev,
                      sub_category:
                        next === String(target.currentValue ?? "") ? storedSub : "",
                    }));
                  }}
                  disabled={vendorCategoriesQuery.isLoading}
                  error={Boolean(validationError)}
                  helperText={validationError ?? " "}
                  inputProps={{ "aria-label": `New value for ${target.label}` }}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {vendorCategories.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : STATIC_SELECT_OPTIONS[target.fieldName] ? (
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  error={Boolean(validationError)}
                  helperText={validationError ?? " "}
                  inputProps={{ "aria-label": `New value for ${target.label}` }}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {STATIC_SELECT_OPTIONS[target.fieldName]!.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    autoFocus
                    error={Boolean(validationError)}
                    helperText={validationError ?? " "}
                    inputProps={{
                      "aria-label": `New value for ${target.label}`,
                      ...(primaryMax ? { maxLength: primaryMax } : {}),
                    }}
                  />
                  {primaryMax && (
                    <CharCounter length={newValue.length} max={primaryMax} />
                  )}
                </>
              )}
              {isMulti && validationError && (
                <Typography
                  sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}
                >
                  {validationError}
                </Typography>
              )}
            </Box>

            {extraFieldDefs.map((f) => {
              if (f.type === "document") {
                const picked = extraDocuments[f.fieldName];
                const pickedUrl = extraDocumentUrls[f.fieldName] ?? null;
                const docMissing = !f.currentAttachment && !picked;
                return (
                  <Box key={f.fieldName}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
                      {f.label}
                    </Typography>
                    <input
                      ref={(el) => {
                        extraDocRefs.current[f.fieldName] = el;
                      }}
                      type="file"
                      hidden
                      onChange={(e) =>
                        setExtraDocuments((prev) => ({
                          ...prev,
                          [f.fieldName]: e.target.files?.[0] ?? null,
                        }))
                      }
                      aria-label={f.label}
                    />
                    {picked && pickedUrl ? (
                      <Box
                        sx={{
                          border: 1,
                          borderColor: "#E0E4EF",
                          borderRadius: 1,
                          px: 1.5,
                          py: 1.25,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          fontSize: 14,
                        }}
                      >
                        <AttachmentDisplay
                          document={{
                            id: -1,
                            owner_type: "facility",
                            owner_id: "",
                            document_type: f.fieldName,
                            service_provider: "local",
                            bucket: "",
                            file: picked.name,
                            download_url: pickedUrl,
                          }}
                          allowBlobUrl
                        />
                        <Button
                          size="small"
                          variant="text"
                          onClick={() =>
                            extraDocRefs.current[f.fieldName]?.click()
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: 12,
                            minWidth: 0,
                          }}
                          aria-label={`Change ${f.label}`}
                        >
                          Change
                        </Button>
                      </Box>
                    ) : f.currentAttachment ? (
                      <Box
                        sx={{
                          border: 1,
                          borderColor: "#E0E4EF",
                          borderRadius: 1,
                          px: 1.5,
                          py: 1.25,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          fontSize: 14,
                        }}
                      >
                        <AttachmentDisplay document={f.currentAttachment} />
                        <Button
                          size="small"
                          variant="text"
                          onClick={() =>
                            extraDocRefs.current[f.fieldName]?.click()
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: 12,
                            minWidth: 0,
                          }}
                          aria-label={`Change ${f.label}`}
                        >
                          Change
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        component="button"
                        type="button"
                        onClick={() =>
                          extraDocRefs.current[f.fieldName]?.click()
                        }
                        aria-label={`Upload ${f.label}`}
                        sx={{
                          border: 1,
                          borderColor: "#E0E4EF",
                          borderRadius: 1,
                          px: 1.5,
                          py: 1.25,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          color: "text.secondary",
                          fontSize: 14,
                          textAlign: "left",
                          bgcolor: "transparent",
                          font: "inherit",
                          width: "100%",
                          "&:hover": { borderColor: "primary.main" },
                          "&:focus-visible": {
                            outline: "2px solid",
                            outlineColor: "primary.main",
                          },
                        }}
                      >
                        <AttachFileIcon sx={{ fontSize: 18 }} />
                        Choose a file
                      </Box>
                    )}
                    {docMissing && (
                      <Typography
                        sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}
                      >
                        {f.label} is required.
                      </Typography>
                    )}
                  </Box>
                );
              }
              if (f.type === "sub_category") {
                const raw = (extraValues[f.fieldName] ?? "").trim();
                const selectError = f.required && !raw ? `${f.label} is required.` : "";
                return (
                  <Box key={f.fieldName}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
                      {f.label}
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={raw}
                      onChange={(e) =>
                        setExtraValues((prev) => ({
                          ...prev,
                          [f.fieldName]: e.target.value,
                        }))
                      }
                      disabled={!newValue.trim() || vendorCategoriesQuery.isLoading}
                      error={Boolean(selectError)}
                      helperText={
                        selectError || (!newValue.trim() ? "Pick a Super Category first." : " ")
                      }
                      inputProps={{ "aria-label": f.label }}
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      {subCategoryOptions.map((opt) => (
                        <MenuItem key={opt.code} value={opt.code}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                );
              }
              if (f.fieldName === "account_type") {
                const currentRaw = (f.currentValue ?? "").trim();
                const raw = (extraValues[f.fieldName] ?? "").trim();
                const accountTypeError =
                  (f.required || currentRaw) && !raw
                    ? `${f.label} is required.`
                    : "";
                return (
                  <Box key={f.fieldName}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
                      {f.label}
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={extraValues[f.fieldName] ?? ""}
                      onChange={(e) =>
                        setExtraValues((prev) => ({
                          ...prev,
                          [f.fieldName]: e.target.value,
                        }))
                      }
                      disabled={accountTypesQuery.isLoading}
                      error={Boolean(accountTypeError)}
                      helperText={accountTypeError || " "}
                      inputProps={{ "aria-label": f.label }}
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      {accountTypeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                );
              }
              const isConfirm = f.type === "confirm";
              const extraMax = textMaxLengthFor(f.fieldName);
              const showConfirmError =
                isConfirm &&
                (extraValues[f.fieldName] ?? "").length > 0 &&
                (extraValues[f.fieldName] ?? "").trim() !==
                  resolveMatchValue(f.matches);
              const lockedByPennyDrop =
                f.fieldName === "account_name" &&
                pennyDropStatus === "verified";
              const isPincodeDerived =
                f.fieldName === "billing_city" || f.fieldName === "billing_country";
              const unlockedByPincodeMiss =
                isPincodeDerived &&
                (pincodeStatus === "idle" || pincodeStatus === "failed");
              const isPanLegalName =
                target.fieldName === "pan_number" &&
                f.fieldName === "legal_name";
              const unlockedByPanLookupMiss =
                isPanLegalName && panLookupStatus === "failed";
              const filledByGstLookup =
                target.fieldName === "vendor_type" &&
                gstLookupStatus === "resolved" &&
                ((f.fieldName === "address_billing" && gstFilled.line) ||
                  (f.fieldName === "location_pincode" && gstFilled.pincode));
              const isLocked =
                (f.type === "readonly" || lockedByPennyDrop) &&
                !unlockedByPincodeMiss &&
                !unlockedByPanLookupMiss;
              const placeholderText =
                f.fieldName === "account_name"
                  ? pennyDropStatus === "verifying"
                    ? "Verifying with the bank…"
                    : lockedByPennyDrop
                      ? "Auto-filled from bank penny-drop"
                      : f.placeholder
                  : isPincodeDerived
                    ? pincodeStatus === "resolving"
                      ? "Resolving from pincode…"
                      : pincodeStatus === "resolved"
                        ? "Auto-filled from pincode"
                        : `Enter ${f.label.toLowerCase()}`
                    : isPanLegalName
                      ? panLookupStatus === "resolving"
                        ? "Looking up legal name from PAN…"
                        : panLookupStatus === "resolved"
                          ? "Auto-filled from PAN"
                          : unlockedByPanLookupMiss
                            ? "Enter legal name manually"
                            : f.placeholder
                      : f.type === "readonly"
                        ? f.placeholder ?? "Auto-filled from IFSC"
                        : f.placeholder;
              const rawExtra = (extraValues[f.fieldName] ?? "").trim();
              const perExtraError = (() => {
                if (f.type === "confirm") return null;
                if (f.type === "readonly") {
                  const currentRaw = (f.currentValue ?? "").trim();
                  return (f.required || currentRaw) && !rawExtra
                    ? `${f.label} is required.`
                    : null;
                }
                if (isLocked) return null;
                if (f.type === "date") {
                  return rawExtra
                    ? futureDateError(f, rawExtra)
                    : `${f.label} is required.`;
                }
                if (!rawExtra) return `${f.label} is required.`;
                if (extraMax && rawExtra.length > extraMax) {
                  return `${f.label} must be ${extraMax} characters or fewer.`;
                }
                if (f.fieldName === "ifsc_code" && ifscStatus === "failed") {
                  return "IFSC code not found. Check the code and try again.";
                }
                return validateFieldValue(f.fieldName, rawExtra);
              })();
              const showFieldError = Boolean(perExtraError);
              const panLookupHint = unlockedByPanLookupMiss
                ? panLookupError
                  ? `${panLookupError} Enter the legal name manually to continue.`
                  : "PAN name auto-fill unavailable. Enter the legal name manually to continue."
                : null;
              const gstLookupHint =
                f.fieldName === "gst" && gstLookupStatus === "failed"
                  ? gstLookupError
                  : null;
              const helperTextForField = showConfirmError
                ? "Should match with new value"
                : panLookupHint
                  ? panLookupHint
                  : perExtraError
                    ? perExtraError
                    : gstLookupHint
                      ? gstLookupHint
                      : filledByGstLookup
                        ? "Auto-filled from the GSTIN's registered address. Edit if needed."
                        : lockedByPennyDrop
                      ? "Auto-filled from bank penny-drop; edit account number or IFSC to change."
                      : " ";
              return (
                <Box key={f.fieldName}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
                    {f.label}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type={f.type === "date" ? "date" : "text"}
                    value={extraValues[f.fieldName] ?? ""}
                    onChange={
                      isLocked
                        ? undefined
                        : (e) =>
                            setExtraValues((prev) => ({
                              ...prev,
                              [f.fieldName]: e.target.value,
                            }))
                    }
                    placeholder={placeholderText}
                    disabled={isLocked}
                    error={showConfirmError || showFieldError}
                    helperText={helperTextForField}
                    inputProps={{
                      "aria-label": f.label,
                      readOnly: isLocked,
                      ...(extraMax ? { maxLength: extraMax } : {}),
                      ...(f.maxDate ? { max: f.maxDate } : {}),
                      ...(isConfirm
                        ? {
                            autoComplete: "off",
                            onPaste: blockClipboard,
                            onCopy: blockClipboard,
                            onCut: blockClipboard,
                            onDrop: blockClipboard,
                          }
                        : {}),
                    }}
                    InputLabelProps={
                      f.type === "date" ? { shrink: true } : undefined
                    }
                  />
                  {extraMax && (
                    <CharCounter
                      length={(extraValues[f.fieldName] ?? "").length}
                      max={extraMax}
                    />
                  )}
                </Box>
              );
            })}

            {showCmPicker && (
              <ReviewerPickers
                showCm
                cm={cm}
                onCmChange={setCm}
                cmHint="required — any one of them reviews this request"
              />
            )}

            {pennyDropStatus === "failed" && pennyDropError && (
              <Alert severity="warning">{pennyDropError}</Alert>
            )}

            {submitMutation.isError && (
              <MutationErrorAlert
                error={submitMutation.error}
                fallback="Something went wrong. Please try again."
              />
            )}
            {sendOtpMutation.isError && (
              <MutationErrorAlert
                error={sendOtpMutation.error}
                fallback="Could not send the verification code. Please try again."
              />
            )}

            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={guardedClose}
                disabled={submitMutation.isLoading || sendOtpMutation.isLoading}
                sx={{
                  minWidth: 120,
                  borderColor: "divider",
                  color: "text.primary",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={submitDisabled || sendOtpMutation.isLoading}
                onClick={() => {
                  const otpFlow =
                    gov.path === "B" && Boolean(otpChannelFor(target.fieldName));
                  const anyAdded = isMulti ? addedEmails.length > 0 : true;
                  if (otpFlow && anyAdded) {
                    sendOtpMutation.mutate();
                  } else {
                    submitMutation.mutate();
                  }
                }}
                startIcon={
                  submitMutation.isLoading || sendOtpMutation.isLoading ? (
                    <CircularProgress size={16} />
                  ) : undefined
                }
                sx={{ height: 44, textTransform: "none", fontWeight: 600 }}
              >
                {primaryActionLabel}
              </Button>
            </Box>
          </>
        )}

        {phase === "otp" && (
          <>
            <Box>
              <Chip
                size="small"
                label={pres.chipLabel}
                sx={{
                  bgcolor: pres.chipBg,
                  color: pres.chipFg,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  height: 26,
                  px: 0.5,
                }}
              />
              <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1 }}>
                {pres.explanation}
              </Typography>
            </Box>

            {otpRows.length > 0 && (
              <Alert
                severity="info"
                icon={false}
                sx={{
                  bgcolor: "#EEF2FF",
                  color: "#1E3A8A",
                  "& .MuiAlert-message": { fontSize: 13 },
                }}
              >
                {otpRows.length === 1 ? (
                  <>
                    Verification code sent to{" "}
                    <strong>{otpRows[0].email}</strong>.
                  </>
                ) : (
                  <>
                    Verification codes sent to{" "}
                    <strong>{otpRows.length} new email addresses</strong>. Enter
                    each code below.
                  </>
                )}
              </Alert>
            )}

            {otpRows.map((row, i) => (
              <Box key={row.requestId}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
                  {otpRows.length > 1 ? row.email : "Verification OTP"}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={row.otp}
                  onChange={(e) => {
                    const next = e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
                    setOtpRows((prev) =>
                      prev.map((r, idx) =>
                        idx === i ? { ...r, otp: next, error: null } : r,
                      ),
                    );
                  }}
                  autoFocus={i === 0}
                  placeholder={"-".repeat(OTP_LENGTH)}
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: OTP_LENGTH,
                    "aria-label": `Verification OTP for ${row.email}`,
                    style: { letterSpacing: 8, textAlign: "center", fontSize: 18 },
                  }}
                  error={Boolean(row.error)}
                  helperText={row.error ?? " "}
                />
              </Box>
            ))}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -1 }}>
              <Button
                variant="text"
                size="small"
                disabled={
                  resendCooldown > 0 ||
                  sendOtpMutation.isLoading ||
                  verifyAndSubmitMutation.isLoading ||
                  resendCount >= OTP_MAX_RESENDS
                }
                onClick={() => sendOtpMutation.mutate()}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12,
                  minWidth: 0,
                }}
              >
                {resendCount >= OTP_MAX_RESENDS
                  ? "Max resends reached"
                  : resendCooldown > 0
                    ? `Resend ${otpRows.length > 1 ? "OTPs" : "OTP"} in ${resendCooldown}s`
                    : `Resend ${otpRows.length > 1 ? "OTPs" : "OTP"}`}
              </Button>
            </Box>

            {verifyAndSubmitMutation.isError && (
              <MutationErrorAlert
                error={verifyAndSubmitMutation.error}
                fallback="Verification failed. Please try again."
              />
            )}
            {sendOtpMutation.isError && (
              <MutationErrorAlert
                error={sendOtpMutation.error}
                fallback="Could not resend the verification code. Please try again."
              />
            )}

            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={guardedClose}
                disabled={
                  verifyAndSubmitMutation.isLoading || sendOtpMutation.isLoading
                }
                sx={{
                  minWidth: 120,
                  borderColor: "divider",
                  color: "text.primary",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={verifyDisabled}
                onClick={() => verifyAndSubmitMutation.mutate()}
                startIcon={
                  verifyAndSubmitMutation.isLoading ? (
                    <CircularProgress size={16} />
                  ) : undefined
                }
                sx={{ height: 44, textTransform: "none", fontWeight: 600 }}
              >
                Verify &amp; apply
              </Button>
            </Box>
          </>
        )}

      </Box>
    </Drawer>
  );
}

export default EditDrawer;
